import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import Api from "src/Config";
import { textTruncate } from "src/helpers/truncateText";

export const TransactionFeedsContext = createContext(null);

type Props = {
  children: ReactElement;
};

const TransactionFeedsProvider: FunctionComponent<Props> = ({ children }) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const pageQuery = queryParams.get("page") || 1;
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: [
      "transaction_feeds",
      // pageNumber,
      pageQuery,
      queryParams.toString(),
    ],
    staleTime: 30000,
    queryFn: async () => {
      const { data } = await Api.get(
        // `tm/transactions?page_size=10&page=${pageNumber + 1}`,
        `tm/transactions?page_size=10&${decodeURIComponent(
          queryParams as any,
        )}`,
      );

      const response = data.results.map(
        (res: {
          id: string;
          amount: string;
          score: number;
          reference: string;
          severity: string;
          status: string;
          is_assigned: boolean;
          created_at: string;
          user: {
            last_name: string;
            first_name: string;
          };
        }) => ({
          id: res.id,
          "transaction ID": res.reference || "N/A",
          name: `${
            !res?.user?.first_name?.toLowerCase() &&
            !res?.user?.last_name?.toLowerCase()
              ? "N/A"
              : textTruncate(
                  `${res.user.first_name?.toLowerCase() || ""} ${
                    res.user.last_name?.toLowerCase() || ""
                  }`,
                  20,
                )
          }`,
          amount: res.amount,
          "fraud score": res.score,
          severity: res.severity || "N/A",
          status: res.status || "N/A",
          date: res.created_at ? new Date(res.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : "N/A",
          is_assigned: res.is_assigned,
        }),
      );

      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      data,
      error,
      isFetching,
      pageCount,
      pageNumber,
      setPageNumber,
      setQueryParams,
      pageQuery,
    }),
    [
      isLoading,
      data,
      error,
      isFetching,
      pageCount,
      pageNumber,
      setPageNumber,
      setQueryParams,
      pageQuery,
    ],
  );
  return (
    <TransactionFeedsContext.Provider value={contextValue}>
      {children}
    </TransactionFeedsContext.Provider>
  );
};

export default TransactionFeedsProvider;
